import type { Vehicle } from '../services/vehicle.service'
import { isFleetTractor, optionalVehicleNumber, prepareVehicleFormPayload } from './vehicleFormContract'

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable)
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined).sort(([a], [b]) => a.localeCompare(b)).map(([key, item]) => [key, stable(item)]))
  return value
}

/** Compare persisted form values, excluding preview mirrors and object-key ordering. */
export function vehicleFormFingerprint(vehicle: Partial<Vehicle>): string {
  let payload: Partial<Vehicle>
  try { payload = prepareVehicleFormPayload(vehicle) } catch { payload = vehicle }
  return JSON.stringify(stable({ ...payload, registration_number: vehicle.registration_number?.trim() || null }))
}

export const PROFILE_CHANGE_GROUPS: Array<{ label: string; fields: string[] }> = [
  { label: 'Type et sous-type', fields: ['type', 'vehicle_subtype', 'container_type'] },
  { label: 'Dimensions et volume', fields: ['length', 'width', 'height', 'capacity_volume'] },
  { label: 'Masses et charge utile', fields: ['empty_weight', 'max_weight_permitted', 'max_combination_weight', 'capacity_weight'] },
  { label: 'Essieux et attelage', fields: ['axle_configuration', 'fifth_wheel'] },
  { label: 'Obstacles fixes', fields: ['obstacles'] },
  { label: 'Équipements et contraintes', fields: ['max_floor_pressure', 'pressure_cell_target_size', 'has_reefer_unit', 'reefer_temperature_range', 'is_hazardous_compatible', 'target_cg_x', 'target_cg_y', 'max_cg_deviation'] },
]

const PROFILE_NUMERIC_FIELDS = new Set(['length', 'width', 'height', 'capacity_volume', 'empty_weight', 'max_weight_permitted', 'max_combination_weight', 'capacity_weight', 'max_floor_pressure', 'pressure_cell_target_size', 'target_cg_x', 'target_cg_y', 'max_cg_deviation'])
function profileFieldChanged(before: Partial<Vehicle>, after: Partial<Vehicle>, field: string): boolean {
  const comparable = (vehicle: Partial<Vehicle>) => {
    const value = (vehicle as Record<string, unknown>)[field]
    // Database decimal strings and equivalent numbers describe the same setting.
    return PROFILE_NUMERIC_FIELDS.has(field)
      ? value == null || value === '' ? null : optionalVehicleNumber(value) ?? value
      : stable(value ?? null)
  }
  return JSON.stringify(comparable(before)) !== JSON.stringify(comparable(after))
}

export function changedProfileGroups(before: Partial<Vehicle>, after: Partial<Vehicle>): string[] {
  return PROFILE_CHANGE_GROUPS.filter(group => group.fields.some(field => profileFieldChanged(before, after, field))).map(group => group.label)
}

/** Group headings are distinct from the actual field names used by the editor. */
export function changedProfileDetails(before: Partial<Vehicle>, after: Partial<Vehicle>): Array<{ label: string; fields: string[] }> {
  const labels: Record<string, string> = {
    type: 'Type d’équipement', vehicle_subtype: 'Sous-type', container_type: 'Sous-type',
    length: 'Longueur', width: 'Largeur', height: 'Hauteur', capacity_volume: 'Volume intérieur calculé',
    empty_weight: 'Poids à vide', max_weight_permitted: after.type === 'CONTAINER' ? 'Masse brute maximale' : 'PTAC',
    max_combination_weight: 'PTRA', capacity_weight: 'Charge utile calculée',
    axle_configuration: 'Essieux', fifth_wheel: 'Sellette', obstacles: 'Obstacles',
    max_floor_pressure: 'Pression max au plancher', pressure_cell_target_size: 'Taille de cellule de pression',
    has_reefer_unit: 'Unité de Réfrigération', reefer_temperature_range: 'Température minimale / Température maximale',
    is_hazardous_compatible: 'Compatible ADR (Matières Dangereuses)', target_cg_x: 'Position longitudinale (X)',
    target_cg_y: 'Position transversale (Y)', max_cg_deviation: 'Déviation maximale tolérée',
  }
  const fieldLabels = (field: string): string[] => {
    if (field !== 'axle_configuration') return [labels[field]]
    const coupling = new Set(['kingPinPosition', 'fifthWheelPosition', 'fifthWheelMaxLoad', 'kingPinHeight', 'fifthWheelHeight'])
    const previous = { ...before.axle_configuration } as Record<string, unknown>
    const proposed = { ...after.axle_configuration } as Record<string, unknown>
    const axles = (config: Record<string, unknown>) => stable(Object.fromEntries(Object.entries(config).filter(([key]) => !coupling.has(key))))
    const result: string[] = []
    if (JSON.stringify(axles(previous)) !== JSON.stringify(axles(proposed))) result.push('Essieux')
    if ([...coupling].some(key => JSON.stringify(stable(previous[key] ?? null)) !== JSON.stringify(stable(proposed[key] ?? null)))) result.push('Sellette')
    return result.length ? result : ['Essieux']
  }
  return PROFILE_CHANGE_GROUPS.flatMap(group => {
    const fields = group.fields.filter(field => profileFieldChanged(before, after, field))
    return fields.length ? [{ label: group.label, fields: [...new Set(fields.flatMap(fieldLabels))] }] : []
  })
}

export function duplicateReference(source: string | null | undefined, existing: Array<Partial<Vehicle>>, attempt = 1): string {
  const used = new Set(existing.map(vehicle => vehicle.registration_number?.trim().toLocaleUpperCase('fr-FR')).filter(Boolean))
  const base = source?.trim() || 'VÉHICULE'
  for (let index = attempt; ; index++) {
    const suffix = `-COPIE-${index}`
    const candidate = `${base.slice(0, 50 - suffix.length)}${suffix}`
    if (!used.has(candidate.toLocaleUpperCase('fr-FR'))) return candidate
  }
}

export function duplicateVehiclePayload(vehicle: Partial<Vehicle>, registration: string): Partial<Vehicle> {
  const copy = { ...vehicle } as Record<string, unknown>
  for (const field of ['id', 'tenant_id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'partner', 'SiteDepot', 'addressDepot']) delete copy[field]
  return prepareVehicleFormPayload({ ...copy, registration_number: registration } as Partial<Vehicle>)
}

export type SectionState = 'complete' | 'partial' | 'empty' | 'optional' | 'not-applicable'
export const SECTION_STATE_LABELS: Record<SectionState, string> = { complete: 'Renseigné', partial: 'Partiellement renseigné', empty: 'À renseigner', optional: 'Optionnel', 'not-applicable': 'Non applicable' }

/** Completeness describes supplied values, never technical or regulatory validation. */
export function vehicleSectionState(vehicle: any, section: string, registrationRequired = false, tractor = isFleetTractor(vehicle || {})): SectionState {
  const number = (field: string) => optionalVehicleNumber(vehicle?.[field])
  const container = vehicle?.type === 'CONTAINER'
  switch (section) {
    case 'preset': return vehicle?.registration_number?.trim() ? 'complete' : registrationRequired ? 'empty' : 'optional'
    case 'size': {
      const values = [number('length'), number('width'), number('height')]
      return values.every(value => value != null && value > 0) ? 'complete' : values.some(value => value != null) ? 'partial' : 'empty'
    }
    case 'weights': {
      const tare = number('empty_weight'), gross = number('max_weight_permitted')
      return tare != null && tare >= 0 && gross != null && gross > 0 ? 'complete' : tare != null || gross != null ? 'partial' : 'empty'
    }
    case 'dimensions': {
      const values = [number('length'), number('width'), number('height'), number('max_weight_permitted'), number('empty_weight')]
      return values.every((value, index) => value != null && (index === 4 ? value >= 0 : value > 0)) ? 'complete' : values.some(value => value != null) ? 'partial' : 'empty'
    }
    case 'axles': {
      if (container) return 'not-applicable'
      const axles = vehicle?.axle_configuration?.axles
      if (!axles?.length) return 'empty'
      return axles.every((axle: any) => axle.id?.trim() && optionalVehicleNumber(axle.position) != null && Number(axle.position) >= 0 && optionalVehicleNumber(axle.maxWeight) != null && Number(axle.maxWeight) > 0) ? 'complete' : 'partial'
    }
    case 'fifth-wheel': {
      if (!tractor && vehicle?.type !== 'SEMI_TRAILER') return 'not-applicable'
      const config = vehicle?.axle_configuration || {}
      const position = optionalVehicleNumber(config[tractor ? 'fifthWheelPosition' : 'kingPinPosition'])
      const load = optionalVehicleNumber(config.fifthWheelMaxLoad)
      return position != null && position >= 0 && load != null && load > 0 ? 'complete' : position != null || load != null ? 'partial' : 'optional'
    }
    case 'obstacles': return tractor ? 'not-applicable' : vehicle?.obstacles?.length ? 'complete' : 'optional'
    case 'pressure': return number('max_floor_pressure') != null ? 'complete' : number('pressure_cell_target_size') != null ? 'partial' : 'optional'
    case 'equipment':
    case 'constraints': {
      const range = vehicle?.reefer_temperature_range
      const hasPressure = section === 'constraints' && number('max_floor_pressure') != null
      const hasEquipment = vehicle?.has_reefer_unit || vehicle?.is_hazardous_compatible
      if (!hasPressure && !hasEquipment) return 'optional'
      if (vehicle?.has_reefer_unit && (optionalVehicleNumber(range?.min) == null || optionalVehicleNumber(range?.max) == null || Number(range.min) > Number(range.max))) return 'partial'
      return 'complete'
    }
    default: return 'optional'
  }
}

export function knownPayloadSummary(vehicles: Array<Partial<Vehicle>>) {
  const applicable = vehicles.filter(vehicle => !isFleetTractor(vehicle))
  const values = applicable.map(vehicle => optionalVehicleNumber(vehicle.capacity_weight)).filter((value): value is number => value != null && value >= 0)
  return { value: values.length ? values.reduce((sum, value) => sum + value, 0) : null, known: values.length, applicable: applicable.length }
}
